/**
 * Card Component - Professional Modern Design
 * Elevated container with variants and hover states
 */

import type { HTMLAttributes, ReactNode } from 'react';

export type CardVariant = 'default' | 'bordered' | 'elevated';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: CardVariant;
  hover?: boolean;
}

export function Card({
  children,
  variant = 'default',
  hover = false,
  className = '',
  ...props
}: CardProps) {
  const baseStyles = 'bg-white rounded-xl border border-neutral-200';

  const variantStyles: Record<CardVariant, string> = {
    default: 'shadow-sm',
    bordered: 'border-2',
    elevated: 'shadow-md',
  };

  const hoverStyles = hover
    ? 'hover:shadow-lg hover:border-neutral-300 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer'
    : '';

  return (
    <div
      className={`${baseStyles} ${variantStyles[variant]} ${hoverStyles} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * CardHeader - Optional header component for more complex headers
 */
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardHeader({ children, className = '', ...props }: CardHeaderProps) {
  return (
    <div
      className={`px-6 py-4 border-b border-neutral-200 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * CardBody - Optional body component for consistent padding
 */
export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardBody({ children, className = '', ...props }: CardBodyProps) {
  return (
    <div className={`p-6 ${className}`} {...props}>
      {children}
    </div>
  );
}

/**
 * CardFooter - Optional footer component
 */
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardFooter({ children, className = '', ...props }: CardFooterProps) {
  return (
    <div
      className={`px-6 py-4 bg-neutral-50 border-t border-neutral-200 rounded-b-xl ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}
